// @flow

import * as mobx from 'mobx'
import type { ObservableMap } from 'mobx'
import PT from 'prop-types'
import * as o from 'rxjs/operators'
import logger from '@vimcar/shared-utils/logger'
import type { Params } from '@vimcar/shared-utils/linkViewModel'
import type { DomainSwitch } from '@vimcar/shared-stores/mobx/DomainSwitch'
import type { Calendar } from '@vimcar/shared-stores/mobx/Calendar'
import type { Inventory } from '@vimcar/shared-stores/mobx/Calendar/Inventories/Inventory'
import type { Users } from '@vimcar/shared-stores/mobx/users'
import type { Subscriber } from '@vimcar/shared-utils/subscriber'
import styles from '@vimcar/shared-components/components/AppLayout/components/AppMinimalHeader/styles.css'

import i18n from '#utils/i18n'
import { O } from '#utils/rx'
import {
  CALENDAR_VIEW,
  PREFERENCES_VIEW_KEY,
  SHOW_WALKTHROUGH_TIMEOUT,
  SIMPLIFIED_VIEW,
  viewToStringMap,
  WALKTHROUGH_NOT_SEEN,
  WALKTHROUGH_SEEN_NOT_SET,
  WALKTHROUGH_SEEN,
} from '#utils/booking'
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_DESKTOP,
} from '#utils/ui'
import { type Kiosk } from '#components/KioskProvider'

const tp = i18n.prefix('components.CreateBooking')

type Props = {}

type Context = {
  domain: DomainSwitch<any>,
  calendar: Calendar,
  kiosk: Kiosk,
  permissions: { userCanManageVehicles: boolean },
  persistentStore: { values: ObservableMap<boolean> },
  users: Users,
  viewport: { is: (string) => boolean },
}

type LayoverStep = {
  position: string,
  selector: string,
  text: string,
  title: string,
}

export type ViewModel = {|
  _showIntro: boolean,
  _showWalkthrough: boolean,
  _startToggleViewWalkThrough: (view: string) => void,
  _startWalkthrough: () => void,
  _view: string | null,
  bookableInventories: Array<Inventory>,
  componentDidMount: () => void,
  handleClickShowCalendar: () => void,
  handleClickShowSimplified: () => void,
  handleCloseWalkThrough: ({ action: string }) => void,
  handleIntroSubmit: (view: string, saveAsDefault: boolean) => void,
  handleSetCalendarView: () => void,
  isCalendarView: boolean,
  isKioskMode: boolean,
  isSimplifiedView: boolean,
  loadDependencies$: Subscriber,
  setTimeframeWalkThroughDelayTimeout: boolean,
  setToggleViewWalkthroughDelayTimeout: boolean,
  showCreateBooking: boolean,
  showIntro: boolean,
  showNoBookableVehiclesWarning: boolean,
  showOnlySubscibedVehicles: boolean,
  showViewSwitches: boolean,
  showWalkthrough: boolean,
  title: string,
  userCanManageVehicles: boolean,
  view: null | string,
  walkThroughSeen: boolean,
  walkthroughSteps: Array<LayoverStep>,
|}

const mkViewModel = (
  { registerObservable, mkSubscriber }: Params,
  _props: Props,
  ctx: Context
): ViewModel => {
  const inventories = ctx.calendar.inventories
  if (!inventories) throw new Error('Loading inventories failed')
  const preferences = ctx.users.optionalSelf.get().preferences
  const stringToSymbol: any = {
    calendar: CALENDAR_VIEW,
    simplified: SIMPLIFIED_VIEW,
  }

  const vm: ViewModel = mobx.observable(({
    _view: stringToSymbol[preferences.map.get(PREFERENCES_VIEW_KEY)],

    _showIntro: !preferences.map.has(PREFERENCES_VIEW_KEY),

    _showWalkthrough: false,

    loadDependencies$: mkSubscriber(),

    get isSimplifiedView() {
      return (
        ctx.viewport.is(BREAKPOINT_MOBILE) ||
        vm._view === SIMPLIFIED_VIEW
      )
    },

    get isCalendarView() {
      return (
        ctx.viewport.is(BREAKPOINT_DESKTOP) &&
        vm._view === CALENDAR_VIEW
      )
    },

    get showOnlySubscibedVehicles() {
      return (
        !!ctx.domain.currentDomain.settings.map &&
        !ctx.domain.currentDomain.settings.map
          .booking.unsubscribedCarsVisibleBooking
      )
    },

    get bookableInventories() {
      return vm.showOnlySubscibedVehicles
        ? inventories.bookableAccessible
        : inventories.bookable
    },

    get showNoBookableVehiclesWarning() {
      return vm.bookableInventories.length === 0
    },

    get showIntro() {
      return (
        vm.bookableInventories.length > 0 &&
        ctx.viewport.is(BREAKPOINT_DESKTOP) &&
        vm._showIntro
      )
    },

    get showViewSwitches() {
      return (
        ctx.viewport.is(BREAKPOINT_DESKTOP) &&
        !vm.showIntro
      )
    },

    get showCreateBooking() {
      return (
        vm.bookableInventories.length > 0 &&
        !vm.showIntro &&
        vm.view !== null
      )
    },

    get showWalkthrough() {
      return (
        ctx.viewport.is(BREAKPOINT_DESKTOP) &&
        vm.showCreateBooking &&
        vm._showWalkthrough &&
        vm.walkthroughSteps.length > 0
      )
    },

    get isKioskMode() {
      return ctx.kiosk.isKioskMode
    },

    get title() {
      if (vm.isSimplifiedView) return tp('simplified-booking')
      if (vm.isCalendarView) return tp('calendar-booking')
      return tp('intro-title')
    },

    get userCanManageVehicles() {
      return ctx.permissions.userCanManageVehicles
    },

    get walkthroughSteps() {
      return [{
        title: tp('title'),
        text: tp('header-tooltip'),
        position: 'bottom',
        selector: `.${styles.viewSwitches}`,
      }]
    },

    walkThroughSeen: WALKTHROUGH_SEEN_NOT_SET,

    setTimeframeWalkThroughDelayTimeout: false,

    get view() {
      return ctx.viewport.is(BREAKPOINT_MOBILE)
        ? SIMPLIFIED_VIEW
        : vm._view
    },

    handleCloseWalkThrough: mobx.action((callback) => {
      if (
        callback.action &&
        callback.action === 'close' &&
        !vm.setTimeframeWalkThroughDelayTimeout
      ) {
        vm.setTimeframeWalkThroughDelayTimeout = window.setTimeout(
          mobx.action(() => {
            vm.walkThroughSeen = WALKTHROUGH_SEEN
          }),
          SHOW_WALKTHROUGH_TIMEOUT
        )
      }
    }),

    _startWalkthrough: mobx.action(() => {
      if (!ctx.viewport.is(BREAKPOINT_DESKTOP)) return

      const KEY = 'didBookingTour'
      if (ctx.persistentStore.values.get(KEY) === true) {
        vm.walkThroughSeen = WALKTHROUGH_NOT_SEEN
        return
      }
      ctx.persistentStore.values.set(KEY, true)
      vm._showWalkthrough = true
    }),

    handleClickShowCalendar: mobx.action(() => {
      if (vm._showIntro) return
      vm._view = CALENDAR_VIEW
    }),

    handleClickShowSimplified: mobx.action(() => {
      if (vm._showIntro) return
      vm._view = SIMPLIFIED_VIEW
    }),

    setToggleViewWalkthroughDelayTimeout: false,

    _startToggleViewWalkThrough: mobx.action((view) => {
      vm._view = view
      vm._showIntro = false
      if (
        ctx.viewport.is(BREAKPOINT_DESKTOP) &&
        !vm.setToggleViewWalkthroughDelayTimeout
      ) {
        vm.setToggleViewWalkthroughDelayTimeout = window.setTimeout(
          vm._startWalkthrough,
          SHOW_WALKTHROUGH_TIMEOUT
        )
      }
    }),

    handleIntroSubmit: mobx.action((view, saveAsDefault) => {
      const observable$ = saveAsDefault
        ? ctx.users.optionalSelf.get().preferences
          .set({ [PREFERENCES_VIEW_KEY]: viewToStringMap[view] })
        : O.of(true)

      registerObservable(
        observable$.pipe(
          o.catchError((err) => {
            logger.error(err)
            return O.empty()
          }),
          o.tap(() => {
            vm._startToggleViewWalkThrough(view)
          })
        )
      )
    }),

    handleSetCalendarView: mobx.action(() => {
      vm._view = CALENDAR_VIEW
    }),

    componentDidMount: () => {
      vm.loadDependencies$.set(
        O.merge(
          inventories.list(),
          ctx.domain.currentDomain.settings.get(),
          ctx.domain.currentDomain.foxboxPreferences.get(),
          ctx.users.optionalSelf.get().preferences.get(),
        ).pipe(
          o.toArray(),
        )
      )
    },
  }: ViewModel))

  return vm
}

mkViewModel.contextTypes = {
  domain: PT.object,
  calendar: PT.object,
  kiosk: PT.object,
  permissions: PT.object,
  persistentStore: PT.object,
  users: PT.object,
  viewport: PT.object,
}

export default mkViewModel
